import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks'
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  MutationDefinition,
} from '@reduxjs/toolkit/query/react'
import {
  BASE_URL,
  DNS_LATENCY_URL,
  PROXY_LATENCY_URL,
  V4_CHECK_URL,
  V4_DOMAIN,
  V6_CHECK_URL,
  V6_DOMAIN,
  VERIFY_DOMAIN,
} from 'gatsby-env-variables'
import { useEffect } from 'react'
import { ApiResponse } from 'store/api/http'
import { baseApi } from 'store/api'

export interface IP {
  ip: string
  type: string
  org: string
  country?: string
  city?: string
  handler: string
  pop: string
  asn?: number
}

export interface DetectionInfo {
  protocol?: string
  uid?: string
  device?: string //name
}

export interface LatencyData {
  host: string
  rtt: string
  ip: string
}

const randomSubDomain = Math.random().toString(36).substr(2, 12)

export const statusApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getOriginIpv4: builder.query({
      query: () => `${V4_CHECK_URL}/ip`,
      transformResponse: (response: ApiResponse<IP>) => response.body,
    }),
    getOriginIpv6: builder.mutation({
      query: () => `${V6_CHECK_URL}/ip`,
      transformResponse: (response: ApiResponse<IP>) => response.body,
    }),
    verifyControlDUsage: builder.query({
      query: () => `https://${randomSubDomain}.${VERIFY_DOMAIN}/ip`,
      transformResponse: (response: ApiResponse<IP>) => response.body,
    }),
    protocolAndResolversDetection: builder.query({
      query: () => `https://${randomSubDomain}.${VERIFY_DOMAIN}/detect`,
      transformResponse: (response: ApiResponse<DetectionInfo>) => response.body,
    }),
    newIpv4: builder.query({
      query: () => `https://${randomSubDomain}.${V4_DOMAIN}/ip`,
      transformResponse: (response: ApiResponse<IP>) => response.body,
    }),
    newIpv6: builder.mutation({
      query: () => `https://${randomSubDomain}.${V6_DOMAIN}/ip`,
      transformResponse: (response: ApiResponse<IP>) => response.body,
    }),
    dnsLatency: builder.mutation({
      query: () => `${DNS_LATENCY_URL}/info`,
      transformResponse: (response: ApiResponse<LatencyData>) => response.body,
    }),
    proxyLatency: builder.mutation({
      query: () => `${PROXY_LATENCY_URL}:42069/info`,
      transformResponse: (response: ApiResponse<LatencyData>) => response.body,
    }),
    proxyAuthCheck: builder.query({
      query: () => `https://${VERIFY_DOMAIN}/ip/authcheck`,
      transformResponse: (response: ApiResponse<IP>) => response.success,
    }),
    nullRouteCheck: builder.mutation({
      query: () => `${BASE_URL}/ip/nullroutecheck`,
      transformResponse: (response: ApiResponse<IP>) => response,
    }),
    reloadConfig: builder.query({
      query: (resolverId: string) => `https://${resolverId}.${VERIFY_DOMAIN}/utility/reload-config`,
      transformResponse: (response: ApiResponse) => response,
    }),
  }),
})
const timeout = 5000
export const {
  useGetOriginIpv6Mutation,
  useNewIpv6Mutation,
  useDnsLatencyMutation,
  useProxyLatencyMutation,
  useNullRouteCheckMutation,
} = statusApi

function wrapMutationWithTimeout<T>(
  mutation: UseMutation<
    MutationDefinition<
      string,
      BaseQueryFn<
        string | FetchArgs,
        unknown,
        FetchBaseQueryError,
        Record<string, undefined>,
        FetchBaseQueryMeta
      >,
      'Status',
      T,
      'api'
    >
  >,
) {
  return () => {
    const [trigger, { data, isLoading, isUninitialized, error }] = mutation()
    useEffect(() => {
      const request = trigger('')
      setTimeout(() => {
        request?.abort()
      }, timeout)
    }, [trigger])
    return { data, isLoading, isUninitialized, error }
  }
}

export const useGetOriginIpv6QueryWithTimeout =
  wrapMutationWithTimeout<IP>(useGetOriginIpv6Mutation)
export const useNewIpv6QueryWithTimeout = wrapMutationWithTimeout<IP>(useNewIpv6Mutation)
export const useDnsLatencyQueryWithTimeout =
  wrapMutationWithTimeout<LatencyData>(useDnsLatencyMutation)
export const useProxyLatencyQueryWithTimeout =
  wrapMutationWithTimeout<LatencyData>(useProxyLatencyMutation)
export const useNullRouteCheckQueryWithTimeout =
  wrapMutationWithTimeout<ApiResponse<IP>>(useNullRouteCheckMutation)

export const {
  useGetOriginIpv4Query,
  useVerifyControlDUsageQuery,
  useProtocolAndResolversDetectionQuery,
  useNewIpv4Query,
  useProxyAuthCheckQuery,
  useLazyReloadConfigQuery,
} = statusApi
